import { useEffect, useRef } from 'react';

/** Legacy default (30 min). Storefront customer idle logout is disabled in AuthContext; admin uses {@link ADMIN_SESSION_IDLE_MS}. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

/** Admin panel only: sign out after this many milliseconds with no pointer/keyboard/scroll activity. */
export const ADMIN_SESSION_IDLE_MS = 30 * 60 * 1000;

const ACTIVITY_THROTTLE_MS = 750;

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
];

/**
 * After `idleMs` with no user activity, calls `onIdle` once.
 * Activity resets the timer. Throttled so high-frequency events do not hurt performance.
 */
export function useIdleSessionTimeout({ enabled, onIdle, idleMs = SESSION_IDLE_MS }) {
  const onIdleRef = useRef(onIdle);
  const timerRef = useRef(null);
  const throttleRef = useRef(0);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onIdleRef.current?.();
      }, idleMs);
    };

    schedule();

    const onActivity = () => {
      const now = Date.now();
      if (now - throttleRef.current < ACTIVITY_THROTTLE_MS) return;
      throttleRef.current = now;
      schedule();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        schedule();
      }
    };

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, onActivity, { passive: true, capture: true });
    });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, onActivity, { capture: true });
      });
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, idleMs]);
}
